import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Monitor,
  Mic,
  Coffee,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Rocket,
  Zap,
  Globe,
  Workflow,
  Cog
} from 'lucide-react';

// WhatsApp Icon SVG Component
const WhatsAppIcon = ({ className = "" }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

// Navigation
const Navigation = () => (
  <nav className="sticky top-0 z-50 bg-cream border-b-4 border-text-dark">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center py-4">
        <Link to="/" className="flex items-center" data-testid="nav-logo">
          <span className="font-poppins font-black text-2xl md:text-3xl tracking-tight text-text-dark">
            BOOST<span className="text-pink">CHANGE</span>
          </span>
        </Link>
        <div className="flex items-center gap-4 md:gap-6">
          <Link to="/impact-boost-academy" className="hidden md:flex items-center gap-2 font-poppins font-semibold text-text-dark hover:text-pink transition-colors" data-testid="nav-back-academy">
            <ArrowLeft className="w-4 h-4" /> Academy
          </Link>
          <button
            onClick={() => document.getElementById('formulario-registro')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-pink text-white font-poppins font-bold uppercase tracking-widest px-4 md:px-6 py-3 border-3 border-text-dark hover:translate-x-0.5 hover:translate-y-0.5 transition-all text-sm md:text-base"
            style={{ boxShadow: '4px 4px 0px #1A1A1A', borderRadius: '0px', border: '3px solid #1A1A1A' }}
            data-testid="nav-cta-inscribete"
          >
            Inscríbete
          </button>
        </div>
      </div>
    </div>
  </nav>
);

// SECTION 1 - HERO
const HeroSection = () => {
  return (
    <section className="bg-cream py-12 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Left Column */}
          <div className="md:col-span-7 space-y-6">
            <div className="space-y-2">
              <div
                className="inline-block bg-cyan text-text-dark px-4 py-2"
                style={{ border: '2px solid #1A1A1A', borderRadius: '0px' }}
              >
                <span className="font-poppins font-bold text-xs uppercase tracking-wide">
                  CURSO: TECH SOCIAL · IA Y AUTOMATIZACIÓN
                </span>
              </div>
              <p className="font-poppins text-sm text-gray-500">
                Impact Boost Academy · 2da Edición
              </p>
            </div>

            <h1 className="font-poppins font-black text-4xl md:text-5xl lg:text-[52px] text-text-dark leading-[1.1]">
              Escala el impacto de tu organización con{' '}
              <span className="text-cyan">tecnología e IA.</span>
            </h1>

            <p className="font-poppins text-base md:text-lg text-gray-700 leading-relaxed">
              Aprende a usar IA, no-code y automatizaciones para ahorrar 10+ horas semanales, crear recursos digitales y construir sistemas que trabajan por ti. Sin programar, sin complicaciones — todo pensado para impulsar tu impacto.
            </p>

            {/* Course Details Card */}
            <div
              className="bg-white p-6"
              style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
            >
              <h3 className="font-poppins font-bold text-lg text-text-dark mb-3">
                Cómo está organizado el curso
              </h3>
              <p className="font-poppins text-sm text-gray-600 mb-4">
                Nuestro enfoque es 100% práctico. Sales con sistemas funcionando, no con teoría — herramientas que usarás desde el día uno.
              </p>
              <div className="space-y-2">
                <p className="font-poppins text-sm text-text-dark">
                  <span className="mr-2">📅</span>
                  <strong>Cuándo:</strong> Inicia el 27 de mayo de 2026 (5 sesiones en vivo).
                </p>
                <p className="font-poppins text-sm text-text-dark">
                  <span className="mr-2">🕡</span>
                  <strong>Horario:</strong> Miércoles de 18:30 a 20:30 (Hora Ecuador).
                </p>
                <p className="font-poppins text-sm text-text-dark">
                  <span className="mr-2">☕</span>
                  <strong>Dinámica:</strong> Clases interactivas + Coffee Hours semanales.
                </p>
                <p className="font-poppins text-sm text-text-dark">
                  <span className="mr-2">🎓</span>
                  <strong>Cierre:</strong> Certificado de participación incluido.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <button
                onClick={() => document.getElementById('formulario-registro')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-pink text-white font-poppins font-bold uppercase tracking-widest px-6 py-4 hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
                style={{ border: '3px solid #1A1A1A', boxShadow: '4px 4px 0px #1A1A1A', borderRadius: '0px' }}
                data-testid="hero-cta-reserva"
              >
                RESERVA TU CUPO
              </button>
            </div>
          </div>

          {/* Right Column */}
          <div className="md:col-span-5 relative">
            <div
              className="w-full overflow-hidden bg-white"
              style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_project-status-62/artifacts/jlyftkr2_WhatsApp%20Image%202026-01-29%20at%204.39.03%20PM.jpeg"
                alt="Alumni del curso Tech Social"
                className="w-full h-auto"
                style={{ display: 'block' }}
              />
            </div>

            <div
              className="absolute -top-3 -right-3 bg-pink text-white px-3 py-2"
              style={{ border: '3px solid #1A1A1A', boxShadow: '4px 4px 0px #1A1A1A', borderRadius: '0px' }}
            >
              <span className="font-poppins font-black text-sm">98% recomienda</span>
            </div>

            <div
              className="absolute -bottom-3 -left-3 bg-lime text-text-dark px-3 py-2"
              style={{ border: '3px solid #1A1A1A', boxShadow: '4px 4px 0px #1A1A1A', borderRadius: '0px' }}
            >
              <span className="font-poppins font-bold text-sm">+800 alumni formados</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// SECTION 2 - CÓMO FUNCIONA
const HowItWorksSection = () => {
  const cards = [
    {
      icon: <Monitor className="w-8 h-8 text-white" />,
      iconBg: "bg-cyan",
      title: "Plataforma virtual 24/7",
      description: "Accede a materiales, grabaciones y entregables desde nuestra plataforma educativa — disponible en cualquier dispositivo durante el programa y 3 años después del cierre."
    },
    {
      icon: <Mic className="w-8 h-8 text-white" />,
      iconBg: "bg-pink",
      title: "5 sesiones en vivo",
      description: "Cada miércoles de 18:30 a 20:30 (hora Ecuador) por Zoom. Clases participativas con ejercicios aplicados a tu organización y casos reales del sector social en América Latina."
    },
    {
      icon: <Coffee className="w-8 h-8 text-text-dark" />,
      iconBg: "bg-lime",
      title: "Coffee Hours semanales",
      description: "Espacios cada semana para resolver dudas en vivo con los facilitadores, profundizar herramientas y conectar con otros líderes sociales que también están construyendo su sistema tech."
    }
  ];

  return (
    <section className="bg-yellow py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark text-center mb-12 uppercase">
          ¿Cómo funciona el curso?
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          {cards.map((card) => (
            <div
              key={card.title}
              className="bg-white p-6 flex flex-col h-full"
              style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
              data-testid={`how-card-${card.title}`}
            >
              <div
                className={`${card.iconBg} p-4 inline-block mb-4 self-start`}
                style={{ border: '3px solid #1A1A1A', borderRadius: '0px' }}
              >
                {card.icon}
              </div>
              <h3 className="font-poppins font-bold text-xl text-text-dark mb-3">{card.title}</h3>
              <p className="font-poppins text-sm text-gray-600 leading-relaxed flex-grow">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// SECTION 3 - PARA QUIÉN ES
const ForWhoSection = () => {
  const cards = [
    {
      number: "1",
      badgeBg: "bg-cyan",
      title: "Trabajas en impacto social",
      description: "Lideras o eres parte de una ONG, fundación o emprendimiento social y buscas formas inteligentes de multiplicar tu capacidad sin contratar más personal."
    },
    {
      number: "2",
      badgeBg: "bg-pink",
      title: "Sabes que la tecnología puede ayudarte",
      description: "Intuyes que la IA y las automatizaciones podrían liberar tu tiempo, pero no sabes por dónde empezar ni qué herramientas son las correctas para tu contexto."
    },
    {
      number: "3",
      badgeBg: "bg-lime",
      title: "Buscas herramientas accesibles",
      description: "Necesitas soluciones que no requieran programadores ni inversiones gigantes — herramientas no-code y de IA que se adapten a la realidad del sector social."
    }
  ];

  return (
    <section className="bg-cream py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark text-center mb-12 uppercase">
          Este curso es para ti si…
        </h2>

        <div className="grid md:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div
              key={card.title}
              className="bg-white p-6 pt-8 relative h-full flex flex-col"
              style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
              data-testid={`forwho-card-${card.number}`}
            >
              <div
                className={`${card.badgeBg} absolute -top-4 -left-4 w-10 h-10 flex items-center justify-center`}
                style={{ border: '3px solid #1A1A1A', borderRadius: '0px' }}
              >
                <span className="font-poppins font-black text-xl text-text-dark">{card.number}</span>
              </div>

              <h3 className="font-poppins font-bold text-2xl text-text-dark mb-4">{card.title}</h3>
              <p className="font-poppins text-base text-gray-600 leading-relaxed flex-grow">{card.description}</p>
            </div>
          ))}
        </div>

        <p className="font-poppins text-sm italic text-gray-500 text-center mt-10">
          No necesitas saber programar — solo ganas de explorar, experimentar y construir.
        </p>
      </div>
    </section>
  );
};

// SECTION 4 - DIFERENCIADORES
const WhyDifferentSection = () => {
  const reasons = [
    {
      badgeBg: "bg-cyan",
      icon: <Workflow className="w-7 h-7 text-text-dark" />,
      title: "Diseñado para el sector social",
      description: "No es un curso genérico de tecnología. Cada herramienta, ejemplo y caso práctico está pensado para los desafíos reales de ONGs, fundaciones y emprendimientos sociales en América Latina."
    },
    {
      badgeBg: "bg-pink",
      icon: <Cog className="w-7 h-7 text-white" />,
      title: "Sales con algo funcionando",
      description: "En 5 semanas construirás sistemas concretos que te ahorrarán horas y te darán más tiempo para enfocarte en lo estratégico."
    },
    {
      badgeBg: "bg-lime",
      icon: <Coffee className="w-7 h-7 text-text-dark" />,
      title: "Acompañamiento humano",
      description: "5 clases en vivo, Coffee Hours semanales, comunidad activa de líderes sociales y facilitadores disponibles. No estarás solo en este proceso."
    }
  ];

  return (
    <section className="bg-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark text-center mb-4 uppercase">
          ¿Por qué Tech Social es diferente?
        </h2>
        <p className="font-poppins text-base text-gray-600 text-center mb-12 max-w-2xl mx-auto">
          En Boost Change hemos formado a más de 800 personas en América Latina. El 98% de nuestros alumni recomendaría este curso a sus colegas.
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          {reasons.map((r) => (
            <div
              key={r.title}
              className="bg-cream p-6 h-full flex flex-col"
              style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
              data-testid={`why-card-${r.title}`}
            >
              <div
                className={`${r.badgeBg} p-3 inline-block mb-4 self-start`}
                style={{ border: '3px solid #1A1A1A', borderRadius: '0px' }}
              >
                {r.icon}
              </div>
              <h3 className="font-poppins font-bold text-xl text-text-dark mb-3">{r.title}</h3>
              <p className="font-poppins text-sm text-gray-600 leading-relaxed flex-grow">{r.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// SECTION 5 - QUÉ VAS A LOGRAR
const ObjectivesSection = () => {
  const objectives = [
    {
      badgeBg: "bg-cyan",
      icon: <Zap className="w-6 h-6 text-text-dark" />,
      title: "Automatizar 10+ horas/semana",
      description: "Identifica y automatiza las tareas repetitivas que te quitan tiempo cada semana, liberando espacio para lo estratégico."
    },
    {
      badgeBg: "bg-pink",
      icon: <Globe className="w-6 h-6 text-white" />,
      title: "Crear tu propia web o micrositio",
      description: "Construye landings, micrositios y prototipos funcionales con Canva IA y herramientas no-code, sin escribir una sola línea de código."
    },
    {
      badgeBg: "bg-lime",
      icon: <Workflow className="w-6 h-6 text-text-dark" />,
      title: "Conectar herramientas en flujos",
      description: "Diseña flujos inteligentes con Zapier, Make y Notion donde formularios, bases de datos, IA y comunicación trabajan juntas."
    },
    {
      badgeBg: "bg-yellow",
      icon: <Cog className="w-6 h-6 text-text-dark" />,
      title: "Implementar tu sistema Tech Social",
      description: "Sales con un sistema funcional, un plan de implementación de 30-60 días y métricas de éxito definidas para tu organización."
    },
    {
      badgeBg: "bg-purple",
      icon: <Rocket className="w-6 h-6 text-white" />,
      title: "Usar IA con criterio ético",
      description: "Aprende los riesgos éticos del uso de IA en el sector social y desarrolla criterios para usarla de forma responsable y estratégica."
    }
  ];

  return (
    <section className="bg-cream py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark text-center mb-12 uppercase">
          Al finalizar, habrás logrado:
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          {objectives.map((obj) => (
            <div
              key={obj.title}
              className="bg-white p-6 relative h-full flex flex-col"
              style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
              data-testid={`objective-card-${obj.title}`}
            >
              <div
                className={`${obj.badgeBg} w-10 h-10 flex items-center justify-center mb-4`}
                style={{ border: '3px solid #1A1A1A', borderRadius: '0px' }}
              >
                {obj.icon}
              </div>

              <h3 className="font-poppins font-bold text-lg text-text-dark mb-2">{obj.title}</h3>
              <p className="font-poppins text-sm text-gray-600 leading-relaxed flex-grow">{obj.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// SECTION 6 - LAS 5 SESIONES
const SessionsSection = () => {
  const [openSession, setOpenSession] = useState(0);

  const sessions = [
    {
      badgeBg: "bg-cyan",
      date: "27 de mayo",
      title: "🌐 Ecosistema Tech Social",
      intro: "Exploramos la tecnología como un ecosistema conectado y estratégico para el sector social. Aprendemos a mapear procesos digitales que integran programas, MEL, fundraising y administración.",
      bullets: [
        "Tendencias Tech Social 2026, IA y herramientas no-code para recursos limitados.",
        "Riesgos éticos del uso de IA y mapeo de procesos digitales en tu organización."
      ],
      deliverables: "Skills y prompts para procesos repetitivos, matrices con apoyo de ChatGPT/Claude/Gemini y mapa de procesos de tu organización."
    },
    {
      badgeBg: "bg-pink",
      date: "3 de junio",
      title: "⚡ Automatizaciones Simples para Ganar Tiempo",
      intro: "Aprendemos a decidir estratégicamente qué automatizar usando criterios de impacto, frecuencia y complejidad. Diseñamos automatizaciones accesibles para tareas cotidianas.",
      bullets: [
        "Criterios para decidir qué automatizar y qué no — con foco en impacto real.",
        "Automatización de reuniones, actas con IA, inscripciones y páginas web con Canva IA."
      ],
      deliverables: "Sistemas mínimos viables, flujos para trabajos repetitivos y una web piloto para tu próximo proyecto o evento."
    },
    {
      badgeBg: "bg-lime",
      date: "10 de junio",
      title: "🎨 Creación Ágil de Contenidos y Prototipos con IA",
      intro: "Nos enfocamos en crear recursos digitales funcionales sin saber programar. Profundizamos en Canva IA para landings completas y usamos Claude Design, Gemini y ChatGPT para diseño gráfico.",
      bullets: [
        "Landings avanzadas con Canva IA y presentaciones generadas con IA.",
        "NotebookLM para sintetizar información y construir conocimiento organizacional."
      ],
      deliverables: "Presentaciones con tu línea de marca, insumos gráficos de alta calidad y un sistema de diseño basado en tu manual de marca."
    },
    {
      badgeBg: "bg-yellow",
      date: "17 de junio",
      title: "🔗 Flujos Inteligentes y Automatizaciones Ágiles",
      intro: "Integramos todo lo aprendido para diseñar flujos mínimos efectivos (MEA). Visualizamos la tecnología como un sistema donde formularios, bases, IA y comunicación trabajan juntas.",
      bullets: [
        "Flujos mínimos efectivos (MEA) usando Zapier, Make y Notion para flujos avanzados.",
        "IA para análisis y reportes — diferencia entre Agentes de IA y Flujos automatizados."
      ],
      deliverables: "Flujo completo conectado (piloto), uso de Claude y ChatGPT para creación de documentos y comparativa Agentes vs Flujos."
    },
    {
      badgeBg: "bg-purple",
      date: "24 de junio",
      title: "🚀 Tu Sistema Tech Social",
      intro: "Afinamos y simplificamos tu flujo Tech Social para que sea claro, funcional y fácil de mantener. Presentas tu solución con un pitch en video (Loom) y participas en retroalimentación entre pares.",
      bullets: [
        "Simplificar flujos tech complejos y crear un pitch efectivo en video.",
        "Retroalimentación entre pares y planificación estratégica de 30-60 días."
      ],
      deliverables: "Sistema Tech Social funcional, plan de implementación para usar IA en tu organización y métricas de éxito definidas."
    }
  ];

  const getCheckColor = (bg) => {
    switch (bg) {
      case 'bg-cyan': return '#1ECAD3';
      case 'bg-pink': return '#F44C7F';
      case 'bg-lime': return '#93D500';
      case 'bg-yellow': return '#FFB71B';
      case 'bg-purple': return '#B14FC5';
      default: return '#1ECAD3';
    }
  };

  return (
    <section className="bg-cream py-16 md:py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark text-center mb-12 uppercase">
          El programa sesión a sesión
        </h2>

        <div className="space-y-4">
          {sessions.map((session, i) => (
            <div
              key={session.title}
              className="bg-white overflow-hidden"
              style={{ border: '3px solid #1A1A1A', borderRadius: '0px' }}
              data-testid={`session-accordion-${i}`}
            >
              <button
                onClick={() => setOpenSession(openSession === i ? -1 : i)}
                className="w-full p-4 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors"
              >
                <div
                  className={`${session.badgeBg} px-3 py-1 flex-shrink-0`}
                  style={{ border: '2px solid #1A1A1A', borderRadius: '0px' }}
                >
                  <span className="font-poppins font-bold text-xs text-text-dark uppercase">
                    Sesión {i + 1}
                  </span>
                </div>
                <span className="font-poppins font-bold text-sm text-gray-500 hidden sm:inline">{session.date}</span>
                <span className="font-poppins font-bold text-base text-text-dark flex-1">{session.title}</span>
                {openSession === i ? (
                  <ChevronUp className="w-5 h-5 text-text-dark flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-dark flex-shrink-0" />
                )}
              </button>

              {openSession === i && (
                <div className="p-5 pt-0 border-t-2 border-gray-200">
                  <p className="font-poppins text-base text-gray-800 mb-4 leading-relaxed font-medium">
                    {session.intro}
                  </p>
                  <ul className="space-y-3 mb-5">
                    {session.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3">
                        <span className="text-pink font-bold mt-0.5">—</span>
                        <span className="font-poppins text-base text-gray-700 leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-start gap-3 bg-gray-100 p-4" style={{ borderRadius: '0px' }}>
                    <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: getCheckColor(session.badgeBg) }} />
                    <span className="font-poppins text-base text-gray-800 leading-relaxed">
                      <strong className="font-black text-text-dark">Qué construirás:</strong> {session.deliverables}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// SECTION 7 - TESTIMONIOS (3 videos in grid)
const TestimonialsSection = () => {
  const videos = [
    {
      id: "-Bw4AjE-GQs",
      name: "Yara",
      badge: "bg-cyan"
    },
    {
      id: "lYe8TJ4S92Q",
      name: "Karen",
      badge: "bg-pink"
    },
    {
      id: "y4BP1eURYdM",
      name: "Sara",
      badge: "bg-lime"
    }
  ];

  return (
    <section className="bg-text-dark py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-white text-center mb-4 uppercase">
          Lo que dicen nuestros alumni
        </h2>
        <p className="font-poppins text-base text-white/80 text-center mb-12 max-w-2xl mx-auto">
          Más de 800 personas formadas en América Latina. Conoce de primera mano cómo Tech Social transformó su forma de trabajar.
        </p>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 max-w-4xl mx-auto mb-12">
          <div
            className="bg-cyan p-4 text-center"
            style={{ border: '3px solid #1A1A1A', boxShadow: '6px 6px 0px #1A1A1A', borderRadius: '0px' }}
          >
            <p className="font-poppins font-black text-2xl md:text-3xl text-text-dark">+800</p>
            <p className="font-poppins font-bold text-xs text-text-dark uppercase mt-1">Alumni formados</p>
          </div>
          <div
            className="bg-lime p-4 text-center"
            style={{ border: '3px solid #1A1A1A', boxShadow: '6px 6px 0px #1A1A1A', borderRadius: '0px' }}
          >
            <p className="font-poppins font-black text-2xl md:text-3xl text-text-dark">98%</p>
            <p className="font-poppins font-bold text-xs text-text-dark uppercase mt-1">Lo recomienda</p>
          </div>
          <div
            className="bg-yellow p-4 text-center"
            style={{ border: '3px solid #1A1A1A', boxShadow: '6px 6px 0px #1A1A1A', borderRadius: '0px' }}
          >
            <p className="font-poppins font-black text-2xl md:text-3xl text-text-dark">10+ h</p>
            <p className="font-poppins font-bold text-xs text-text-dark uppercase mt-1">Ahorradas/semana</p>
          </div>
        </div>

        {/* 3 Video Cards Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {videos.map((video, i) => (
            <div
              key={video.id}
              className="bg-white p-4"
              style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
              data-testid={`testimonial-video-${i}`}
            >
              <div className="mb-4">
                <iframe
                  src={`https://www.youtube.com/embed/${video.id}`}
                  title={`Testimonio Alumni ${video.name}`}
                  className="w-full"
                  style={{ aspectRatio: '9/16', maxHeight: '500px', border: 'none' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>

              <p className="font-poppins font-bold text-base text-text-dark text-center mb-3">
                {video.name}
              </p>

              <div className="flex justify-center">
                <div
                  className={`${video.badge} px-3 py-1`}
                  style={{ border: '2px solid #1A1A1A', borderRadius: '0px' }}
                >
                  <span className="font-poppins font-bold text-xs uppercase text-text-dark">
                    Alumni · Tech Social
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// SECTION 8 - PRECIOS
const PricingSection = () => {
  const benefits = [
    "5 sesiones en vivo y prácticas",
    "Acceso a plataforma y grabaciones",
    "Todos los entregables y plantillas",
    "Coffee Hours semanales",
    "Certificado verificable"
  ];

  return (
    <section className="bg-cream py-16 md:py-20">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark text-center mb-12 uppercase">
          Invierte en tu organización
        </h2>

        <div
          className="bg-white p-8"
          style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
        >
          <div
            className="bg-pink text-white px-4 py-2 mb-6 text-center"
            style={{ border: '3px solid #1A1A1A', borderRadius: '0px' }}
          >
            <span className="font-poppins font-bold text-sm uppercase">🔥 EARLY BIRD — VÁLIDO HASTA EL 22 DE MAYO</span>
          </div>

          <div className="text-center mb-6">
            <p className="font-poppins text-lg text-gray-400 line-through mb-1">Precio regular: $211 USD</p>
            <p className="font-poppins font-black text-5xl md:text-6xl text-cyan mb-3">$147.70 USD</p>

            <div
              className="bg-lime inline-block px-4 py-2"
              style={{ border: '2px solid #1A1A1A', borderRadius: '0px' }}
            >
              <span className="font-poppins font-bold text-sm text-text-dark">Ahorras $63.30</span>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            {benefits.map((benefit) => (
              <div key={benefit} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#1ECAD3' }} />
                <span className="font-poppins text-base text-text-dark">{benefit}</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => document.getElementById('formulario-registro')?.scrollIntoView({ behavior: 'smooth' })}
            className="w-full bg-pink text-white font-poppins font-bold uppercase tracking-widest py-4 hover:translate-x-0.5 hover:translate-y-0.5 transition-all text-center"
            style={{ border: '3px solid #1A1A1A', boxShadow: '4px 4px 0px #1A1A1A', borderRadius: '0px' }}
            data-testid="pricing-cta-button"
          >
            QUIERO MI DESCUENTO Y ASEGURAR MI CUPO
          </button>
        </div>

        <a
          href="https://wa.me/593982525226?text=Hola%20Boost%20Change,%20me%20interesa%20inscribir%20a%20mi%20equipo%20en%20el%20curso%20Tech%20Social.%20%C2%BFMe%20ayudan%20con%20informaci%C3%B3n%20sobre%20tarifas%20grupales?"
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-yellow mt-8 p-5 hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
          style={{ border: '3px solid #1A1A1A', boxShadow: '6px 6px 0px #1A1A1A', borderRadius: '0px' }}
          data-testid="pricing-group-whatsapp"
        >
          <p className="font-poppins font-black text-base text-text-dark mb-1">
            ¿Tu organización quiere inscribir a más de una persona?
          </p>
          <span className="inline-flex items-center gap-2 font-poppins font-bold text-sm text-text-dark">
            <WhatsAppIcon className="w-4 h-4" style={{ color: '#25D366' }} />
            Escríbenos por WhatsApp para tarifas grupales
            <ArrowRight className="w-4 h-4" />
          </span>
        </a>
      </div>
    </section>
  );
};

// SECTION 9 - FORMULARIO
const RegistrationSection = () => {
  return (
    <section id="formulario-registro" className="bg-cyan py-16 md:py-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark mb-4 uppercase">
          Empieza a transformar cómo trabaja tu organización
        </h2>

        <p className="font-poppins text-lg text-text-dark mb-8">
          En 5 semanas, sin experiencia técnica ni grandes presupuestos, construirás sistemas que te ahorran tiempo y escalan tu impacto.
        </p>

        <div
          className="bg-white p-8 text-center"
          style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
        >
          <h3 className="font-poppins font-bold text-2xl text-text-dark mb-4">
            Asegura tu cupo hoy
          </h3>
          <p className="font-poppins text-sm text-gray-600 mb-6 leading-relaxed">
            Llena el formulario de pre-registro. Al finalizar, recibirás inmediatamente un correo con el enlace para realizar tu pago y confirmar tu espacio.
          </p>

          <a
            href="https://form.jotform.com/261254597743668"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-pink text-white font-poppins font-bold uppercase tracking-widest px-8 py-4 hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
            style={{ border: '3px solid #1A1A1A', boxShadow: '4px 4px 0px #1A1A1A', borderRadius: '0px' }}
            data-testid="jotform-cta"
          >
            Llenar formulario de inscripción
            <Rocket className="w-5 h-5" />
          </a>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="font-poppins text-sm text-gray-500 mb-2">¿Prefieres hablar primero?</p>
            <a
              href="https://wa.me/593982525226"
              target="_blank"
              rel="noopener noreferrer"
              className="font-poppins font-bold text-text-dark hover:underline"
              data-testid="registration-whatsapp"
            >
              Escríbenos al +593 982525226
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// SECTION 10 - FAQ
const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(-1);

  const faqs = [
    {
      question: "¿Necesito saber programar?",
      answer: "No. Tech Social está diseñado específicamente para personas sin experiencia técnica. Todas las herramientas que verás son no-code o asistidas por IA — funcionan con clics, instrucciones en lenguaje natural y plantillas listas para usar."
    },
    {
      question: "¿Qué herramientas de IA vamos a usar?",
      answer: "Trabajaremos con ChatGPT, Claude, Gemini, Canva IA, NotebookLM, Zapier, Make y Notion. Te enseñamos cómo elegir la correcta para cada caso y cómo combinarlas en flujos efectivos."
    },
    {
      question: "¿Cómo funciona la plataforma virtual?",
      answer: "Al confirmar tu inscripción recibirás acceso a nuestra plataforma educativa con todos los materiales, entregables y grabaciones. Disponible 24/7 en cualquier dispositivo durante el programa y 30 días después del cierre."
    },
    {
      question: "¿Las sesiones quedan grabadas?",
      answer: "Sí. Todas las sesiones quedan grabadas y disponibles en la plataforma para que las revises cuando lo necesites."
    },
    {
      question: "¿Qué son los Coffee Hours?",
      answer: "Espacios semanales abiertos e incluidos en tu inscripción. Resuelves dudas en vivo, profundizas los temas de la semana y conectas con otros líderes del sector social."
    },
    {
      question: "¿Puedo inscribir a más de una persona de mi equipo?",
      answer: "¡Claro! Escríbenos a hola@boost-change.com o al WhatsApp +593 982525226 para conocer nuestras tarifas grupales."
    },
    {
      question: "¿Cuál es la política de reembolso?",
      answer: "Realizamos reembolsos completos hasta 7 días antes del inicio (hasta el 20 de mayo de 2026). Pasada esa fecha no hay reembolso, pero puedes transferir tu cupo a otra persona."
    },
    {
      question: "¿Recibo un certificado al finalizar?",
      answer: "Sí. Participantes que completen al menos 4 de las 5 sesiones reciben certificado digital emitido por Impact Boost Academy — Boost Change."
    }
  ];

  return (
    <section className="bg-white py-16 md:py-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark text-center mb-12 uppercase">
          Preguntas frecuentes
        </h2>

        <div className="space-y-0">
          {faqs.map((faq, i) => (
            <div key={faq.question} className="border-b-2 border-text-dark" data-testid={`faq-item-${i}`}>
              <button
                onClick={() => setOpenFAQ(openFAQ === i ? -1 : i)}
                className="w-full py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
              >
                <span className="font-poppins font-bold text-base text-text-dark pr-4">{faq.question}</span>
                {openFAQ === i ? (
                  <ChevronUp className="w-5 h-5 text-text-dark flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-dark flex-shrink-0" />
                )}
              </button>

              {openFAQ === i && (
                <div className="pb-5">
                  <p className="font-poppins text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-text-dark py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <Link to="/" className="font-poppins font-black text-2xl text-white">
          BOOST<span className="text-pink">CHANGE</span>
        </Link>
        <div className="flex gap-6">
          <Link to="/" className="font-poppins text-white/80 hover:text-pink transition-colors">Inicio</Link>
          <Link to="/impact-boost-academy" className="font-poppins text-white/80 hover:text-pink transition-colors">Academy</Link>
          <Link to="/sistemas-de-impacto" className="font-poppins text-white/80 hover:text-pink transition-colors">Sistemas</Link>
          <Link to="/boost-ops" className="font-poppins text-white/80 hover:text-pink transition-colors">Boost Ops</Link>
        </div>
        <p className="font-poppins text-white/60 text-sm">2026 Boost Change</p>
      </div>
    </div>
  </footer>
);

// Main Page Component
const TechSocialPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-cream" data-testid="tech-social-page">
      <Navigation />
      <main>
        <HeroSection />
        <HowItWorksSection />
        <ForWhoSection />
        <WhyDifferentSection />
        <ObjectivesSection />
        <SessionsSection />
        <TestimonialsSection />
        <PricingSection />
        <RegistrationSection />
        <FAQSection />
      </main>
      <Footer />
    </div>
  );
};

export default TechSocialPage;
